import { useEffect, useState } from 'react';
import { fetchPluginMenu } from './api';
import type { PluginMenuEntry, PluginSurface } from './types';

/**
 * Fetches the plugin menu entries for the given surface ONCE on
 * mount. Returns `{ entries, loading, error }`.
 *
 * Not memoized across components — each mount fetches fresh. Plugin
 * installs are rare and require a panel restart in P0, so cache-
 * staleness isn't worth the complexity.
 */
export function usePluginMenu(surface: PluginSurface): {
  entries: PluginMenuEntry[];
  loading: boolean;
  error: string | null;
} {
  const [entries, setEntries] = useState<PluginMenuEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPluginMenu(surface)
      .then((rows) => {
        if (!cancelled) {
          setEntries(rows ?? []);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('[usePluginMenu] failed', e);
          setError(e?.message ?? 'failed to load plugin menu');
          setEntries([]);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [surface]);

  return { entries, loading, error };
}
